import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';

interface LoadingModalState {
  showModalPDF: boolean;
}

const initialState: LoadingModalState = { showModalPDF: false };

type Action = { type: 'SHOW_MODAL_PDF' } | { type: 'HIDE_MODAL_PDF' };

function loadingModalReducer(
  state: LoadingModalState,
  action: Action,
): LoadingModalState {
  switch (action.type) {
    case 'SHOW_MODAL_PDF':
      return { ...state, showModalPDF: true };
    case 'HIDE_MODAL_PDF':
      return { ...state, showModalPDF: false };
    default:
      return state;
  }
}

interface LoadingModalContextValue {
  showModalPDF: boolean;
  showModal: () => void;
  hideModal: () => void;
}

const LoadingModalContext = createContext<LoadingModalContextValue | null>(
  null,
);

export function LoadingModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(loadingModalReducer, initialState);

  const showModal = useCallback(() => dispatch({ type: 'SHOW_MODAL_PDF' }), []);
  const hideModal = useCallback(() => dispatch({ type: 'HIDE_MODAL_PDF' }), []);

  const value = useMemo(
    () => ({
      showModalPDF: state.showModalPDF,
      showModal,
      hideModal,
    }),
    [state.showModalPDF, showModal, hideModal],
  );

  return (
    <LoadingModalContext.Provider value={value}>
      {children}
    </LoadingModalContext.Provider>
  );
}

export function useLoadingModal() {
  const ctx = useContext(LoadingModalContext);
  if (!ctx)
    throw new Error('useLoadingModal must be used within LoadingModalProvider');
  return ctx;
}
